import axios from "../axios-interceptor.js";
import { DEFAULT_LANGUAGE } from "../utils/formTranslations.js";

const BACKEND_API = process.env.REACT_APP_BACKEND_API;

const resolvePublicFormTheme = (data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return null;
    }

    return (
        data.theme ||
        data.themeSetting ||
        data.themeSettings ||
        data.shareLink?.theme ||
        data.shareLink?.themeSetting ||
        data.shareLink?.themeSettings ||
        null
    );
};

const normalizePublicFormResponse = (data) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) {
        return data;
    }

    const theme = resolvePublicFormTheme(data);

    if (!theme || data.theme === theme) {
        return data;
    }

    return {
        ...data,
        theme,
    };
};

// Get public form by share token
export const getPublicForm = async (shareToken, type="") => {
    let responseObj = { status: "", message: "", data: null };
    let encodedShareToken = encodeURIComponent(shareToken);
    let api = `${BACKEND_API}/api/public/form/${encodedShareToken}`
    if(type === "ongoing") {
        api = `${BACKEND_API}/ongoing/form?shareToken=${encodedShareToken}`
    }
    try {
        let res = await axios({
            method: "GET",
            url: api,
            // No tenantId or auth headers for public endpoints
        });

        responseObj.status = res.status;
        responseObj.data = normalizePublicFormResponse(res.data?.result || res.data);
        responseObj.message = res.data?.message || "Success";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to fetch form";
    }

    return responseObj;
};

// Master data for a dropdown on a public form. Public visitors have no auth
// token, so this goes through the share-token route instead of /master-data.
export const getPublicSingleMasterData = async (dataId, shareToken) => {
    let responseObj = { status: "", message: "", data: null };
    if (!dataId || !shareToken) {
        return { ...responseObj, status: 400, message: "Missing master data id or share token" };
    }
    try {
        let res = await axios({
            method: "GET",
            url: `${BACKEND_API}/api/public/form/master/${encodeURIComponent(dataId)}/${encodeURIComponent(shareToken)}`,
            // No tenantId or auth headers for public endpoints
        });
        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Success";
    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to fetch master data";
    }
    return responseObj;
};

export const getPublicNotificationTemplates = async (shareToken, spaceId) => {
    let responseObj = { status: "", message: "", data: null };
    let encodedShareToken = encodeURIComponent(shareToken);
    let api = `${BACKEND_API}/api/public/form/notification/template/${encodedShareToken}`
    try {
        let res = await axios({
            method: "GET",
            url: api,
            headers: {
                "Content-Type": "application/json",
                spaceId: spaceId
            },
            // No tenantId or auth headers for public endpoints
        });

        responseObj.status = res.status;
        responseObj.data = normalizePublicFormResponse(res.data?.result || res.data);
        responseObj.message = res.data?.message || "Success";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to fetch form";
    }

    return responseObj;
};

// Submit public form
export const submitPublicForm = async (shareToken, formData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/form/${shareToken}/submit`,
            data: {
                formData: formData,
                submittedAt: new Date().toISOString(),
                submitterInfo: {
                    userAgent: navigator.userAgent,
                    timestamp: Date.now(),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
            },
            // No tenantId or auth headers for public endpoints
        });

        responseObj.status = res.status;
        responseObj.data = res.data;
        responseObj.message = res.data?.message || "Form submitted successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to submit form";
    }

    return responseObj;
};

// Generate share link for form
export const generateShareLink = async (formId, options = {}) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/form/share`,
            data: {
                formId,
                spaceId: options.spaceId || null,
                nonValidated: options.nonValidated || false,
                expiryDays: options.expiryDays || 30,
                allowMultipleSubmissions: options.allowMultipleSubmissions !== undefined ? options.allowMultipleSubmissions : true,
                description: options.description || '',
                language: options.language || DEFAULT_LANGUAGE,
                theme: options.theme || { headerColor: '#141bcb', showCompanyLogo: false, companyLogoUrl: null },
            },
            headers: {
                tenantId: localStorage.getItem("tenantId"), // Auth required for admin functions
            },
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Share link generated successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to generate share link";
    }

    return responseObj;
};

// Share Public LInk
export const sharePublicLink = async (postData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/link/share`,
            data: postData,
            headers: {
                tenantId: localStorage.getItem("tenantId"), // Auth required for admin functions
            },
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Link shared successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to share link";
    }

    return responseObj;
};
// Get form shares
export const getFormShares = async (formId) => {
    let responseObj = { status: "", message: "", data: { shares: [] } };

    try {
        let res = await axios({
            method: "GET",
            url: `${BACKEND_API}/api/form/${formId}/shares`,
            headers: {
                tenantId: localStorage.getItem("tenantId"), // Auth required for admin functions
            },
        });

        responseObj.status = res.status;
        responseObj.data = { shares: res.data?.result || [] };
        responseObj.message = res.data?.message || "Success";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to fetch shares";
    }

    return responseObj;
};

// Revoke share link
export const revokeShareLink = async (shareToken) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "DELETE",
            url: `${BACKEND_API}/api/form/share/${shareToken}`,
            headers: {
                tenantId: localStorage.getItem("tenantId"), // Auth required for admin functions
            },
        });

        responseObj.status = res.status;
        responseObj.data = res.data;
        responseObj.message = res.data?.message || "Share link revoked successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to revoke share link";
    }

    return responseObj;
};

// Send OTP for email verification (public form)
export const sendPublicFormOTP = async (email, shareToken) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/form/${shareToken}/send-otp`,
            data: {
                email: email,
                purpose: "form_submission"
            }
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "OTP sent successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to send OTP";
    }

    return responseObj;
};

// Verify OTP for public form
export const verifyPublicFormOTP = async (email, otp, fullName, shareToken) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/form/${shareToken}/verify-otp`,
            data: {
                email: email,
                otp: otp,
                fullName: fullName,
                purpose: "form_submission"
            }
        });

        const payload = res.data || {};
        const otpVerified = payload.result?.otpVerified === true;

        responseObj.status = payload.status ?? res.status;
        responseObj.success = otpVerified;
        responseObj.data = payload;
        responseObj.message = payload.message || (otpVerified ? "OTP verified successfully" : "OTP verification failed");

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Invalid OTP";
        responseObj.success = false;
    }

    return responseObj;
};

// Submit public form request (POST for new, PUT for update)
export const submitPublicFormRequest = async (shareToken, postData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: postData.requestId ? "PUT" : "POST",
            url: `${BACKEND_API}/api/public/form/request/${shareToken}`,
            data: postData
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Request submitted successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to submit request";
    }

    return responseObj;
};

// Submit public form with email verification
export const submitPublicFormWithVerification = async (shareToken, formData, email, fullName, verificationToken) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/public/form/${shareToken}/submit`,
            data: {
                formData: formData,
                email: email,
                fullName: fullName,
                verificationToken: verificationToken,
                submittedAt: new Date().toISOString(),
                submitterInfo: {
                    userAgent: navigator.userAgent,
                    timestamp: Date.now(),
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
            }
        });

        responseObj.status = res.status;
        responseObj.data = res.data;
        responseObj.message = res.data?.message || "Form submitted successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to submit form";
    }

    return responseObj;
};

// Upload attachment for public form
export const uploadPublicFormAttachment = async (requestId, shareToken, file, tenantId) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        const formData = new FormData();
        formData.append('image', file);

        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/api/upload/${requestId}/${encodeURIComponent(shareToken)}`,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
                tenantId: tenantId
            }
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "File uploaded successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to upload file";
    }

    return responseObj;
};

// Download attachment for public form
export const downloadPublicFormAttachment = async (requestId, fileName, shareToken, tenantId) => {
    let downloadResponse = {};

    try {
        let res = await axios({
            method: "GET",
            url: `${BACKEND_API}/api/public-form/download/${requestId}/${fileName}/${encodeURIComponent(shareToken)}`,
            headers: {
                tenantId: tenantId
            }
        });

        if (res && res.data) {
            // Add success flag to match the expected structure in handlePreview
            downloadResponse = {
                ...res.data,
                success: true
            };
        }

    } catch (error) {
        downloadResponse = {
            success: false,
            message: error.response?.data?.message || 'Failed to download file',
            error: error
        };
    }

    return downloadResponse;
};

// Delete attachment for public form
export const deletePublicFormAttachment = async (requestId, fileName, shareToken, tenantId) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "DELETE",
            url: `${BACKEND_API}/api/delete/${requestId}/${fileName}/${encodeURIComponent(shareToken)}`,
            headers: {
                tenantId: tenantId
            }
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "File deleted successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to delete file";
    }

    return responseObj;
};

export const shareOngoingPublicLink = async (postData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "POST",
            url: `${BACKEND_API}/generate/form?requestId=${postData.requestId}&formId=${postData.formId}`,
            data: {emails: postData.emails, language: postData.language || DEFAULT_LANGUAGE, allowMultipleSubmissions: postData.allowMultipleSubmissions || false, allowPreviousForms: postData.allowPreviousForms || false},
            headers: {
                tenantId: localStorage.getItem("tenantId"), // Auth required for admin functions
            },
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Link shared successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to share link";
    }

    return responseObj;
};

export const submitOngoingFormRequest = async (shareToken, postData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: postData.requestId ? "PUT" : "POST",
            url: `${BACKEND_API}/ongoing/form?shareToken=${encodeURIComponent(shareToken)}`,
            data: postData
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Request submitted successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to submit request";
    }

    return responseObj;
};

// Save an ongoing form as a draft. Hits the same endpoint as a submission but
// carries `isDraft`, which tells the backend to write the values onto the
// request (and onto the current step in the workflow) without moving the
// request off the step it is currently on.
export const saveOngoingFormDraft = async (shareToken, postData) => {
    let responseObj = { status: "", message: "", data: null };

    try {
        let res = await axios({
            method: "PUT",
            url: `${BACKEND_API}/ongoing/form?shareToken=${encodeURIComponent(shareToken)}`,
            data: { ...postData, isDraft: true }
        });

        responseObj.status = res.status;
        responseObj.data = res.data?.result || res.data;
        responseObj.message = res.data?.message || "Draft saved successfully";

    } catch (error) {
        responseObj.status = error.response?.status || 500;
        responseObj.message = error.response?.data?.message || "Failed to save draft";
    }

    return responseObj;
};

const publicFormServices = {
    getPublicForm,
    getPublicNotificationTemplates,
    submitPublicForm,
    generateShareLink,
    getFormShares,
    revokeShareLink,
    sendPublicFormOTP,
    verifyPublicFormOTP,
    submitPublicFormWithVerification,
    submitPublicFormRequest,
    uploadPublicFormAttachment,
    downloadPublicFormAttachment,
    deletePublicFormAttachment,
    shareOngoingPublicLink,
    submitOngoingFormRequest,
    saveOngoingFormDraft
};

export default publicFormServices;
